// ============================================
// ChatScreen — Production wrapper for PatientChatEngine
//
// Routes to the correct data source:
// - Real lab report present → live data source (real pipeline)
// - Mock/demo mode → demo data source
//
// CLINICAL SAFETY: Demo data sources are NEVER served to real
// patients in production. When a real lab report exists, the live
// data source powers the chat with real biomarker data, real
// protocol pipeline, and real safety rules.
// ============================================

import { useState, useEffect, Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { useApp } from '../../context/AppContext';
import { getAuthUid } from '../../services/firebase';
import { PatientChatEngine } from '../mobile/PatientChatEngine';
import { LiveChatEngine } from '../mobile/LiveChatEngine';
import { demoDataSource } from '../../services/chat/demoDataSource';
import { maleDemoDataSource } from '../../services/chat/maleDemoDataSource';
import { createLiveDataSource } from '../../services/chat/liveDataSource';
import { createNurseOrchestrator } from '../../services/nurse/NurseOrchestrator';
import type { NurseOrchestrator } from '../../services/nurse/NurseOrchestrator';
import { PageShell } from '../shared/PageShell';
import { useBackgroundAnalysis } from '../../hooks/useBackgroundAnalysis';
import type { ChatDataSource } from '../../types/analystBar';

// ── Error Boundary (Fix 6) ──
interface ErrorBoundaryProps {
  onReset: () => void;
  children: ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}
class ChatErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ChatErrorBoundary] Pipeline crash:', error, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <PageShell>
          <div className="flex flex-col items-center justify-center min-h-screen px-6">
            <div className="max-w-sm w-full text-center">
              <h2 className="font-display text-xl font-bold mb-4">
                HEMOLOGIX<span style={{ color: '#E11D48' }}>.AI</span>
              </h2>
              <div className="p-4 rounded-lg bg-bg-card border border-bg-tertiary">
                <p className="text-text-primary text-sm font-medium mb-2">
                  Something went wrong
                </p>
                <p className="text-text-secondary text-xs leading-relaxed mb-4">
                  The analysis pipeline encountered an error. Your data is safe.
                </p>
                <button
                  onClick={this.props.onReset}
                  className="text-sm font-medium px-4 py-2 rounded-lg"
                  style={{ background: '#E11D48', color: '#FFFFFF' }}
                >
                  Return to Home
                </button>
              </div>
            </div>
          </div>
        </PageShell>
      );
    }
    return this.props.children;
  }
}

export function ChatScreen() {
  const { state, dispatch } = useApp();
  const { analysisStatus, analysisError } = useBackgroundAnalysis();
  const [liveSource, setLiveSource] = useState<ChatDataSource | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const orchestratorRef = useState<NurseOrchestrator | null>(null);

  const hasRealLab = !state.mockMode && state.labReport && !state.labReport.isMockData;
  const isAnalyzing = analysisStatus === 'running';

  // Debug: log state transitions
  console.log('[ChatScreen] state:', { hasRealLab, isAnalyzing, analysisStatus, loading, hasLiveSource: !!liveSource, hasLabReport: !!state.labReport, mockMode: state.mockMode });
  const patientSex = state.triageData?.sex === 'male' ? 'Male' as const : 'Female' as const;
  const patientAge = state.triageData?.age;

  // Initialize live data source + NurseOrchestrator when real lab data is available
  useEffect(() => {
    if (!hasRealLab) return;
    if (liveSource) return; // already initialized

    setLoading(true);
    // Box selection: HRT for female (launch focus), supplements as default
    const box = patientSex === 'Female' ? 'hrt' as const : 'supplements' as const;

    // Resolve patient ID from auth, with fallback to ephemeral ID
    getAuthUid()
      .catch(() => `patient-${Date.now().toString(36)}`) // fallback for edge case (auth failure)
      .then((resolvedPatientId) => {
        // Boot NurseOrchestrator — wires all 10 NRS modules
        const orch = createNurseOrchestrator({
          sessionId: `session-${Date.now().toString(36)}`,
          patientId: resolvedPatientId,
          sex: patientSex === 'Female' ? 'female' : 'male',
          age: patientAge,
          reproductiveStatus: state.triageData?.reproductiveStatus ?? 'not_applicable',
          box,
        });

        // Initialize orchestrator then create live data source with it
        return orch
          .init()
          .then(() => createLiveDataSource('hemologix', patientSex, box, patientAge, orch))
          .then((source) => {
            orchestratorRef[1](orch);
            setLiveSource(source);
            setLoading(false);
          })
          .finally(() => {
            // Cleanup will be handled by the returned function below
          });
      })
      .catch((err) => {
        console.error('[ChatScreen] Failed to create live data source:', err);
        setLiveError(err instanceof Error ? err.message : 'Failed to initialize');
        setLoading(false);
      });

    // Cleanup: stop orchestrator on unmount (best effort, may not have ref yet)
    return () => {
      if (orchestratorRef[0]) {
        orchestratorRef[0].stop();
      }
    };
  }, [hasRealLab, patientSex, liveSource]);

  // Loading state — initializing live data source
  if (hasRealLab && loading) {
    return (
      <PageShell>
        <div className="flex flex-col items-center justify-center min-h-screen px-6">
          <div className="max-w-sm w-full text-center">
            <h2 className="font-display text-xl font-bold mb-4">
              HEMOLOGIX<span style={{ color: '#E11D48' }}>.AI</span>
            </h2>
            <p className="text-text-secondary text-sm">Preparing your results...</p>
          </div>
        </div>
      </PageShell>
    );
  }

  // Error state — live source failed to initialize
  if (hasRealLab && liveError) {
    return (
      <PageShell>
        <div className="flex flex-col items-center justify-center min-h-screen px-6">
          <div className="max-w-sm w-full text-center">
            <h2 className="font-display text-xl font-bold mb-4">
              HEMOLOGIX<span style={{ color: '#E11D48' }}>.AI</span>
            </h2>
            <div className="p-4 rounded-lg bg-bg-card border border-bg-tertiary">
              <p className="text-text-primary text-sm font-medium mb-2">
                Unable to load your results
              </p>
              <p className="text-text-secondary text-xs leading-relaxed mb-4">
                {liveError}
              </p>
              <button
                onClick={() => dispatch({ type: 'SET_SCREEN', payload: 'landing' })}
                className="text-sm font-medium px-4 py-2 rounded-lg"
                style={{ background: '#E11D48', color: '#FFFFFF' }}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  // Real lab data + live source + orchestrator ready → LiveChatEngine (spec-driven nurse)
  if (hasRealLab && liveSource && orchestratorRef[0]) {
    return (
      <ChatErrorBoundary onReset={() => dispatch({ type: 'SET_SCREEN', payload: 'landing' })}>
        <LiveChatEngine dataSource={liveSource} orchestrator={orchestratorRef[0]} />
      </ChatErrorBoundary>
    );
  }

  // Captain is still analyzing uploaded labs — show progress
  if (isAnalyzing) {
    return (
      <PageShell>
        <div className="flex flex-col items-center justify-center min-h-screen px-6">
          <div className="max-w-sm w-full text-center">
            <h2 className="font-display text-xl font-bold mb-4">
              HEMOLOGIX<span style={{ color: '#E11D48' }}>.AI</span>
            </h2>
            <p className="text-text-secondary text-sm">Analyzing your lab results...</p>
            <div style={{ marginTop: 16, width: 40, height: 40, margin: '16px auto', border: '3px solid #E5E7EB', borderTopColor: '#E11D48', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          </div>
        </div>
      </PageShell>
    );
  }

  // Analysis failed — show error with retry
  if (analysisError) {
    return (
      <PageShell>
        <div className="flex flex-col items-center justify-center min-h-screen px-6">
          <div className="max-w-sm w-full text-center">
            <h2 className="font-display text-xl font-bold mb-4">
              HEMOLOGIX<span style={{ color: '#E11D48' }}>.AI</span>
            </h2>
            <div className="p-4 rounded-lg bg-bg-card border border-bg-tertiary">
              <p className="text-text-primary text-sm font-medium mb-2">
                Analysis failed
              </p>
              <p className="text-text-secondary text-xs leading-relaxed mb-4">
                {analysisError}
              </p>
              <button
                onClick={() => dispatch({ type: 'SET_SCREEN', payload: 'landing' })}
                className="text-sm font-medium px-4 py-2 rounded-lg"
                style={{ background: '#E11D48', color: '#FFFFFF' }}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  // No real lab data in production → block demo
  if (!state.mockMode) {
    return (
      <PageShell>
        <div className="flex flex-col items-center justify-center min-h-screen px-6">
          <div className="max-w-sm w-full text-center">
            <h2 className="font-display text-xl font-bold mb-4">
              HEMOLOGIX<span style={{ color: '#E11D48' }}>.AI</span>
            </h2>
            <div className="p-4 rounded-lg bg-bg-card border border-bg-tertiary">
              <p className="text-text-primary text-sm font-medium mb-2">
                Upload your lab results to get started
              </p>
              <p className="text-text-secondary text-xs leading-relaxed mb-4">
                Upload a lab report to receive your personalized health analysis and protocol.
              </p>
              <button
                onClick={() => dispatch({ type: 'SET_SCREEN', payload: 'landing' })}
                className="text-sm font-medium px-4 py-2 rounded-lg"
                style={{ background: '#E11D48', color: '#FFFFFF' }}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  // Mock/demo mode — safe to use demo data sources
  const dataSource = state.triageData?.sex === 'male'
    ? maleDemoDataSource
    : demoDataSource;

  return (
    <ChatErrorBoundary onReset={() => dispatch({ type: 'SET_SCREEN', payload: 'landing' })}>
      <PatientChatEngine dataSource={dataSource} />
    </ChatErrorBoundary>
  );
}
